import { z } from 'zod';
import { catalogItemDetails } from './catalog';

/**
 * Schemas for `POST /catalog-items/from-project-item` — promoting a project
 * line item (Cabinet/Panel/Drawer/...) into the global Catalog and optionally pinning
 * it to the actor's Library in a single transaction.
 * Hardware rows are intentionally excluded because they already reference the
 * dedicated hardware product catalog.
 *
 * Behaviour notes (mirrored in the API):
 *   - 409 `CATALOG_ALREADY_PROMOTED` if the source already has `catalogItemId`.
 *   - 409 `CATALOG_SKU_TAKEN` if the source's SKU collides with an existing
 *     CatalogItem (user must rename then retry).
 *   - 409 `PROJECT_ITEMS_PROJECT_TERMINAL` if the source's project is in a
 *     terminal status (ACCEPTED/REJECTED).
 *   - 403 if `addToLibrary=true` and the actor lacks `library:create`.
 *   - 404 `PROJECT_ITEMS_NOT_FOUND` if the source row is missing or
 *     soft-deleted.
 */
export const promoteSourceEnum = z.enum([
  'cabinet',
  'panel',
  'countertop',
  'element',
  'lighting',
  'drawer',
]);
export type PromoteSourceValue = z.infer<typeof promoteSourceEnum>;

export const createCatalogItemFromProjectItemRequest = z.object({
  source: promoteSourceEnum,
  id: z.string().min(1),
  addToLibrary: z.boolean().optional().default(false),
});
export type CreateCatalogItemFromProjectItemRequest = z.infer<
  typeof createCatalogItemFromProjectItemRequest
>;

export const createCatalogItemFromProjectItemResponse = z.object({
  catalogItem: catalogItemDetails,
  addedToLibrary: z.boolean(),
});
export type CreateCatalogItemFromProjectItemResponse = z.infer<
  typeof createCatalogItemFromProjectItemResponse
>;
